import { useState } from 'react';
import { ClipboardCheck, X } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { PrintDoc } from '../print/PrintDoc';
import { attribuerNumero } from '../../lib/souches';
import { dansPeriode } from '../shared/PeriodeFiltre';

const inputCls =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

// Arrêté de tournée du coursier : avance reçue, dépenses justifiées, reliquat.
export const ArreteCoursier = ({ fin, utilisateur, onClose }) => {
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const [du, setDu] = useState(aujourdhui);
  const [au, setAu] = useState(aujourdhui);
  const [recu, setRecu] = useState(false);

  const compte = fin.comptes.find((c) => c.id === 'crs');
  const mvs = fin.mouvements.filter((m) => dansPeriode(m.date, du, au));
  const avances = mvs.filter((m) => m.type === 'Transfert' && m.compteCibleId === 'crs');
  const restitutions = mvs.filter((m) => m.type === 'Transfert' && m.compteId === 'crs');
  const depenses = mvs.filter((m) => m.type === 'Dépense' && m.compteId === 'crs');
  const reglements = fin.reglements.filter(
    (r) => r.compteId === 'crs' && dansPeriode(r.date, du, au)
  );

  const t = (l, k = 'montant') => Number(l.reduce((s, x) => s + Number(x[k] || 0), 0).toFixed(3));
  const totalAvances = t(avances);
  const totalRestitue = t(restitutions);
  const totalDepenses = t(depenses);
  const totalReglements = t(reglements);
  const justifie = Number((totalDepenses + totalReglements).toFixed(3));
  const reliquat = Number((totalAvances - justifie - totalRestitue).toFixed(3));

  if (recu)
    return (
      <PrintDoc
        type="Arrêté de tournée — Caisse Coursier"
        numero={attribuerNumero('recu_paiement', `ARR-${du}-${au}`)}
        date={aujourdhui}
        format="a4"
        tiers={`Période du ${du} au ${au} — arrêté établi par ${utilisateur || '—'}`}
        lignes={[
          ['Avances reçues', `${avances.length} opération(s) — ${fmt(totalAvances, 3)} TND`],
          ...avances.map((m) => [`  ${m.date} · ${m.piece ?? ''}`, `${fmt(m.montant, 3)} TND`]),
          ['Dépenses justifiées', `${depenses.length} pièce(s) — ${fmt(totalDepenses, 3)} TND`],
          ...depenses.map((m) => [
            `  ${m.date} · ${m.categorie ?? 'Divers'} · ${m.libelle ?? ''}`,
            `${fmt(m.montant, 3)} TND`,
          ]),
          ...(reglements.length
            ? [
                ['Règlements de tiers payés', `${reglements.length} — ${fmt(totalReglements, 3)} TND`],
                ...reglements.map((r) => [
                  `  ${r.date} · ${r.beneficiaire ?? ''}`,
                  `${fmt(r.montant, 3)} TND`,
                ]),
              ]
            : []),
          ...(restitutions.length
            ? [['Déjà restitué à la caisse', `${fmt(totalRestitue, 3)} TND`]]
            : []),
        ]}
        totaux={[
          ['Total avances reçues', `${fmt(totalAvances, 3)} TND`],
          ['Total justifié (dépenses + règlements)', `${fmt(justifie, 3)} TND`],
          ['Déjà restitué', `${fmt(totalRestitue, 3)} TND`],
          ['RELIQUAT À RESTITUER', `${fmt(reliquat, 3)} TND`],
          ['Solde théorique Caisse Coursier', `${fmt(compte?.solde ?? 0, 3)} TND`],
        ]}
        mentions="Le coursier reconnaît avoir reçu les avances listées, justifié les dépenses par pièces et devoir restituer le reliquat indiqué à la caisse du Pont-Bascule."
        signatures={['Le coursier', 'La direction / Pont-Bascule']}
        onClose={() => {
          setRecu(false);
          onClose();
        }}
      />
    );

  return (
    <div data-testid="arrete-coursier-overlay" className="fixed inset-0 z-[65] flex items-center justify-center bg-slate-900/60 p-4">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-slate-200 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight text-slate-900">
              <ClipboardCheck size={18} /> Arrêté de tournée coursier
            </h2>
            <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
              Avance reçue · dépenses justifiées · reliquat à rendre
            </p>
          </div>
          <button type="button" data-testid="arrete-close" onClick={onClose} className="border border-slate-200 p-2 text-slate-500 hover:bg-slate-900 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={label}>Du</label>
              <input data-testid="arrete-du" type="date" value={du} onChange={(e) => setDu(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={label}>Au</label>
              <input data-testid="arrete-au" type="date" value={au} onChange={(e) => setAu(e.target.value)} className={inputCls} />
            </div>
          </div>

          <dl data-testid="arrete-recap" className="border border-slate-200">
            {[
              ['Avances reçues', `${fmt(totalAvances, 3)} TND`, 'arrete-avances'],
              ['Dépenses justifiées', `${fmt(totalDepenses, 3)} TND`, 'arrete-depenses'],
              ['Règlements de tiers payés', `${fmt(totalReglements, 3)} TND`, 'arrete-reglements'],
              ['Déjà restitué', `${fmt(totalRestitue, 3)} TND`, 'arrete-restitue'],
            ].map(([k, v, id]) => (
              <div key={k} className="flex justify-between border-b border-slate-100 px-3 py-2 last:border-b-0">
                <dt className="text-xs uppercase tracking-[0.08em] text-slate-500">{k}</dt>
                <dd data-testid={id} className="tabular font-data text-sm font-semibold text-slate-900">{v}</dd>
              </div>
            ))}
          </dl>

          <div className="flex items-center justify-between border-2 border-slate-900 bg-slate-900 px-4 py-3 text-white">
            <span className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-300">Reliquat à restituer</span>
            <span data-testid="arrete-reliquat" className="tabular font-data text-xl font-semibold">{fmt(reliquat, 3)} TND</span>
          </div>
          <p className="font-data text-[11px] text-slate-500">
            Solde théorique de la Caisse Coursier (toutes périodes) : {fmt(compte?.solde ?? 0, 3)} TND
          </p>
        </div>

        <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            data-testid="arrete-imprimer"
            onClick={() => setRecu(true)}
            className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-slate-700"
          >
            Imprimer l'arrêté
          </button>
          <button type="button" onClick={onClose} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};
